import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getHot, searchItems } from "@/utils/catalog";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 450);
    return () => clearTimeout(timer);
  }, [query]);

  const results = useMemo(() => (query ? searchItems(query) : []), [query]);

  const popular = useMemo(() => getHot("movie", 3).concat(getHot("tv", 3)), []);

  usePageMeta(
    query ? `搜索“${query}”_豆花视频` : "搜索影视_豆花视频",
    query
      ? `豆花视频搜索“${query}”的相关影视内容结果，共找到 ${results.length} 条匹配内容。`
      : "在豆花视频搜索电影、电视剧、综艺等影视内容，快速找到你想看的影片。"
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) {
      setSearchParams({ q });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-10">
      <div className="max-w-3xl mx-auto text-center mb-10">
        <h1 className="text-2xl md:text-3xl font-black text-foreground-950 mb-3">
          搜索影视内容
        </h1>
        <p className="text-sm text-foreground-500 mb-6">
          输入片名、演员、导演或类型关键词，快速定位你想看的内容
        </p>
        <form onSubmit={handleSubmit} className="relative max-w-xl mx-auto">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="例如：科幻、深渊回响、电影…"
            className="w-full h-12 pl-12 pr-28 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
          <button
            type="submit"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 px-5 h-9 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
        <p className="mt-4 text-xs text-foreground-400">
          提示：搜索结果为前端演示数据，用于展示匹配效果。
        </p>
      </div>

      {!query ? (
        <div>
          <SectionHeading title="大家都在看" subtitle="还没有搜索关键词？先从热门内容开始" />
          <MovieGrid
            items={popular}
            columns="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
          />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-background-200">
            <p className="text-sm text-foreground-600">
              关键词
              <span className="mx-1.5 font-semibold text-primary-600">“{query}”</span>
              共找到 <span className="font-semibold text-foreground-900">{results.length}</span> 条结果
            </p>
          </div>

          {!loading && results.length === 0 ? (
            <div>
              <div className="text-center py-14">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400 text-3xl mb-4">
                  <i className="ri-search-eye-line"></i>
                </div>
                <h2 className="text-base font-semibold text-foreground-900 mb-2">
                  没有找到与“{query}”相关的内容
                </h2>
                <p className="text-sm text-foreground-500">
                  换个关键词试试，或者看看下面的热门推荐吧。
                </p>
              </div>
              <div className="mt-6">
                <SectionHeading title="热门推荐" subtitle="为你精选的高人气影视内容" />
                <MovieGrid
                  items={popular}
                  columns="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
                />
              </div>
            </div>
          ) : (
            <MovieGrid
              items={results}
              loading={loading}
              skeletonCount={6}
              columns="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
            />
          )}
        </>
      )}
    </div>
  );
}